import { useState, useEffect } from 'react';
import { supabase } from '../providers/supabase/supabase';

// Types for the raw database data
interface RawArtistData {
    artist_id: string;
    snapshot_date: string;
    cm_artist_score: string;
    cm_artist_rank: string;
    sp_followers: string;
    sp_monthly_listeners: string;
    sp_popularity: string;
    sp_playlist_total_reach: string;
    spotify_playlist_count: string;
    ins_followers: string;
    ins_engagement_rate: string;
    ycs_subscribers: string;
    ycs_views: string;
    youtube_monthly_video_views: string;
    youtube_engagement_rate: string;
    tiktok_followers: string;
    tiktok_likes: string;
    tiktok_engagement_rate: string;
    facebook_followers: string;
    facebook_likes: string;
    ts_followers: string; // Twitter
    soundcloud_followers: string;
    soundcloud_plays: string;
    deezer_fans: string;
    shazam_count: string;
    pandora_listeners_28_day: string;
    genius_views: string;
    wikipedia_views: string;
    bandsintown_followers: string;
    songkick_fans: string;
}

// Clean, typed interface for the component
export interface ArtistMetrics {
    artist_id: string;
    snapshot_date: Date;
    
    // Core metrics
    cm_artist_score: number | null;
    cm_artist_rank: number | null;
    
    // Spotify
    spotify_followers: number | null;
    spotify_monthly_listeners: number | null;
    spotify_popularity: number | null;
    spotify_playlist_reach: number | null;
    spotify_playlist_count: number | null;
    
    // Social platforms
    instagram_followers: number | null;
    instagram_engagement_rate: number | null;
    youtube_subscribers: number | null;
    youtube_views: number | null;
    youtube_monthly_views: number | null;
    youtube_engagement_rate: number | null;
    tiktok_followers: number | null;
    tiktok_likes: number | null;
    tiktok_engagement_rate: number | null;
    facebook_followers: number | null;
    facebook_likes: number | null;
    twitter_followers: number | null;
    
    // Other platforms
    soundcloud_followers: number | null;
    soundcloud_plays: number | null;
    deezer_fans: number | null;
    shazam_count: number | null;
    pandora_listeners: number | null;
    genius_views: number | null;
    wikipedia_views: number | null;
    bandsintown_followers: number | null;
    songkick_fans: number | null;
    
    // Calculated fields
    total_social_followers: number;
    growth_trend: 'up' | 'down' | 'stable' | null;
    career_stage: string;    
    spotify_url?: string;
    instagram_url?: string;
    facebook_url?: string;
    website_url?: string;
}

// Safe number conversion with multiple timestamp format support
const safeParseNumber = (value: string | null | undefined): number | null => {
    if (!value || value === '' || value === 'null' || value === 'undefined') return null;
    const num = parseFloat(value.replace(/,/g, ''));
    return isNaN(num) ? null : num;
};

// Handle multiple timestamp formats
const parseSnapshotDate = (dateString: string): Date => {
    try {
        // Try ISO format first
        if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
            return new Date(dateString);
        }
        
        // Try other common formats
        const formats = [
            dateString, // Raw attempt
            `${dateString}T00:00:00Z`, // Add time if missing
            dateString.replace(/[-/]/g, '-'), // Normalize separators
        ];
        
        for (const format of formats) {
            const date = new Date(format);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }
        
        // Fallback to current date if parsing fails
        console.warn(`Could not parse date: ${dateString}, using current date`);
        return new Date();
    } catch (error) {
        console.warn(`Error parsing date: ${dateString}`, error);
        return new Date();
    }
};

// Determine career stage based on metrics
const calculateCareerStage = (metrics: Partial<ArtistMetrics>): string => {
    const monthlyListeners = metrics.spotify_monthly_listeners || 0;
    const artistRank = metrics.cm_artist_rank || 999999;
    
    if (monthlyListeners > 1000000 || artistRank < 1000) return 'Mainstream';
    if (monthlyListeners > 100000 || artistRank < 10000) return 'Developing';
    if (monthlyListeners > 10000 || artistRank < 50000) return 'Emerging';
    return 'New';
};

// Transform raw data to clean metrics
const transformArtistData = (raw: RawArtistData): ArtistMetrics => {
    const spotify_followers = safeParseNumber(raw.sp_followers);
    const instagram_followers = safeParseNumber(raw.ins_followers);
    const youtube_subscribers = safeParseNumber(raw.ycs_subscribers);
    const tiktok_followers = safeParseNumber(raw.tiktok_followers);
    const facebook_followers = safeParseNumber(raw.facebook_followers);
    const twitter_followers = safeParseNumber(raw.ts_followers);
    
    const total_social_followers = [
        spotify_followers,
        instagram_followers,
        youtube_subscribers,
        tiktok_followers,
        facebook_followers,
        twitter_followers
    ].reduce((sum, val) => sum + (val || 0), 0);
    
    const metrics: ArtistMetrics = {
        artist_id: raw.artist_id,
        snapshot_date: parseSnapshotDate(raw.snapshot_date),
        
        // Core metrics
        cm_artist_score: safeParseNumber(raw.cm_artist_score),
        cm_artist_rank: safeParseNumber(raw.cm_artist_rank),
        
        // Spotify
        spotify_followers,
        spotify_monthly_listeners: safeParseNumber(raw.sp_monthly_listeners),
        spotify_popularity: safeParseNumber(raw.sp_popularity),
        spotify_playlist_reach: safeParseNumber(raw.sp_playlist_total_reach),
        spotify_playlist_count: safeParseNumber(raw.spotify_playlist_count),
        
        // Social platforms
        instagram_followers,
        instagram_engagement_rate: safeParseNumber(raw.ins_engagement_rate),
        youtube_subscribers,
        youtube_views: safeParseNumber(raw.ycs_views),
        youtube_monthly_views: safeParseNumber(raw.youtube_monthly_video_views),
        youtube_engagement_rate: safeParseNumber(raw.youtube_engagement_rate),
        tiktok_followers,
        tiktok_likes: safeParseNumber(raw.tiktok_likes),
        tiktok_engagement_rate: safeParseNumber(raw.tiktok_engagement_rate),
        facebook_followers,
        facebook_likes: safeParseNumber(raw.facebook_likes),
        twitter_followers,
        
        // Other platforms
        soundcloud_followers: safeParseNumber(raw.soundcloud_followers),
        soundcloud_plays: safeParseNumber(raw.soundcloud_plays),
        deezer_fans: safeParseNumber(raw.deezer_fans),
        shazam_count: safeParseNumber(raw.shazam_count),
        pandora_listeners: safeParseNumber(raw.pandora_listeners_28_day),
        genius_views: safeParseNumber(raw.genius_views),
        wikipedia_views: safeParseNumber(raw.wikipedia_views),
        bandsintown_followers: safeParseNumber(raw.bandsintown_followers),
        songkick_fans: safeParseNumber(raw.songkick_fans),
        
        // Calculated fields
        total_social_followers,
        growth_trend: null, // Will be calculated with historical data
        career_stage: ''
    };
    
    metrics.career_stage = calculateCareerStage(metrics);
    
    if (raw.artists) {
        console.log('🔗 FOUND ARTIST URLs:', raw.artists);
        (metrics as any).spotify_url = raw.artists.spotify_url;
        (metrics as any).instagram_url = raw.artists.instagram_url; 
        (metrics as any).facebook_url = raw.artists.facebook_url;
        (metrics as any).website_url = raw.artists.website_url;
        (metrics as any).artist_name = raw.artists.name;
    } else {
        console.log('❌ NO ARTIST URLs FOUND in raw data:', raw);
    }

    return metrics;
};

export const useArtistMetrics = (artistId: string | null | undefined) => {
    const [metrics, setMetrics] = useState<ArtistMetrics | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!artistId) {
            setMetrics(null);
            return;
        }

        const fetchArtistMetrics = async () => {
            setLoading(true);
            setError(null);

            try {
                // Get the latest snapshot for this artist
                const { data: latestData, error: latestError } = await supabase
                    .from('artists_history')
                    .select(`
                        *,
                        artists!artist_id(
                            spotify_url,
                            instagram_url,
                            facebook_url,
                            website_url,
                            name
                        )
                    `)
                    .eq('artist_id', artistId)
                    .order('snapshot_date', { ascending: false })
                    .limit(1)
                    .single();

                if (latestError) {
                    if (latestError.code === 'PGRST116') {
                        // No data found
                        setMetrics(null);
                        return;
                    }
                    throw latestError;
                }

                if (!latestData) {
                    setMetrics(null);
                    return;
                }

                // Transform the raw data
                const transformedMetrics = transformArtistData(latestData);

                // 🎯 ADD THIS CODE HERE:
                console.log('🔍 Fetching URLs for artist_id:', artistId);

                let artistUrls = null;

                // Try as string first
                try {
                    const result1 = await supabase
                        .from('artists')
                        .select('spotify_url, instagram_url, facebook_url, website_url, name, description')
                        .eq('id', artistId)
                        .single();
                    
                    if (!result1.error && result1.data) {
                        artistUrls = result1.data;
                        console.log('✅ SUCCESS with string:', artistUrls);
                    }
                } catch (e) {
                    console.log('❌ String query failed:', e);
                }

                // If that didn't work, try as number
                if (!artistUrls) {
                    try {
                        const result2 = await supabase
                            .from('artists')
                            .select('spotify_url, instagram_url, facebook_url, website_url, name, description')
                            .eq('id', parseInt(artistId))
                            .single();
                        
                        if (!result2.error && result2.data) {
                            artistUrls = result2.data;
                            console.log('✅ SUCCESS with number:', artistUrls);
                        }
                    } catch (e) {
                        console.log('❌ Number query failed:', e);
                    }
                }

                // Apply URLs to metrics if found
                if (artistUrls) {
                    (transformedMetrics as any).spotify_url = artistUrls.spotify_url;
                    (transformedMetrics as any).instagram_url = artistUrls.instagram_url;
                    (transformedMetrics as any).facebook_url = artistUrls.facebook_url;
                    (transformedMetrics as any).website_url = artistUrls.website_url;
                    (transformedMetrics as any).artist_name = artistUrls.name;
                    (transformedMetrics as any).bio = artistUrls.description; 
                    console.log('✅ URLS ADDED TO METRICS');
                } else {
                    console.log('❌ NO URLS FOUND FOR ARTIST:', artistId);
                }

                // Optional: Calculate growth trend by comparing with previous snapshot

                // Optional: Calculate growth trend by comparing with previous snapshot
                try {
                    const { data: previousData } = await supabase
                        .from('artists_history')
                        .select('sp_monthly_listeners, sp_followers')
                        .eq('artist_id', artistId)
                        .order('snapshot_date', { ascending: false })
                        .range(1, 1)
                        .single();

                    if (previousData) {
                        const currentListeners = transformedMetrics.spotify_monthly_listeners || 0;
                        const previousListeners = safeParseNumber(previousData.sp_monthly_listeners) || 0;
                        
                        if (previousListeners > 0) {
                            const growth = (currentListeners - previousListeners) / previousListeners;
                            if (growth > 0.05) transformedMetrics.growth_trend = 'up';
                            else if (growth < -0.05) transformedMetrics.growth_trend = 'down';
                            else transformedMetrics.growth_trend = 'stable';
                        }
                    }
                } catch (growthError) {
                    // Growth calculation failed, but main data is still valid
                    console.warn('Could not calculate growth trend:', growthError);
                }

                setMetrics(transformedMetrics);

            } catch (err) {
                console.error('Error fetching artist metrics:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch artist metrics');
                setMetrics(null);
            } finally {
                setLoading(false);
            }
        };

        fetchArtistMetrics();
    }, [artistId]);

    return { metrics, loading, error };
};

// Helper hook for getting historical data (for charts)
export const useArtistHistory = (artistId: string | null | undefined, limit: number = 10) => {
    const [history, setHistory] = useState<ArtistMetrics[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!artistId) {
            setHistory([]);
            return;
        }

        const fetchHistory = async () => {
            setLoading(true);
            setError(null);

            try {
                const { data, error: historyError } = await supabase
                    .from('artists_history')
                    .select('*')
                    .eq('artist_id', artistId)
                    .order('snapshot_date', { ascending: false })
                    .limit(limit);

                if (historyError) throw historyError;

                const transformedHistory = (data || []).map(transformArtistData);
                setHistory(transformedHistory);

            } catch (err) {
                console.error('Error fetching artist history:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch artist history');
                setHistory([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [artistId, limit]);

    return { history, loading, error };
};
